import { Button, Col,  Row, Typography } from "antd";
import CustomTag from "./Tags/CustomTag";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import LowGrainShutOffMode from "src/consts/LowGrainShutOffMode";
import { formatNumber, formatTimeSpan, minutesToSeconds } from "src/utils/formatting";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";

const { Text } = Typography;

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

interface DryingPageStateTableProps
{
    binDto: PremierPlusBinDTO | null | undefined;
}

const DryingPageStateTable : React.FC<DryingPageStateTableProps> = ({ binDto }) => {

    const valueTextStyle = {
        fontFamily:'monospace', 
        fontWeight:'bolder'
    }

    const labelTextStyle={
        fontWeight:'bold',
        fontSize:'13px'
    }

    const lgsoModeString = useMemo (( ) => {
        switch(binDto?.settings?.dryerSettings?.lgsoMode) {
            case LowGrainShutOffMode.Auto:
                return 'Auto';
            case LowGrainShutOffMode.Bypass:
                return 'Bypass';
            case LowGrainShutOffMode.TimedBypass:
                return 'Timed Bypass';
            default: 
                return 'Unknown';
        }
    }, [binDto]);

    const useCelsius = useMemo ( () => {
        return binDto?.platformSettings?.useMetricSystem;
    }, [binDto]);

    const lowGrain = binDto?.lowGrainShutOff;

    const doubleCellSpan = 5;

    return(
        <div>
            <Row align="middle">
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>Bin Name:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text style={{ fontWeight:'bolder' }}>{binDto?.dryingOperation?.targetStorageBinName ?? 'Unknown'}</Text>
                </Col>
                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={{...labelTextStyle, fontSize:'12px'}}>Minutes to next sample</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text style={valueTextStyle}>{formatTimeSpan(minutesToSeconds(binDto?.dryingOperation?.minutesToNextSample))}</Text>
                </Col>
            </Row>
      
            <Row>
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>Machine:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    {binDto?.isMachineOn ? 
                    <CustomTag icon={<CheckCircleOutlined />} label="ON" color={"green"}/> : 
                    <CustomTag icon={<CloseOutlined />} label="OFF" color={"red"}/>}
                </Col>
                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={labelTextStyle}>Cont. Flow/Aux Augers</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    {   
                        binDto?.augers?.some(a => a.isOn === true) ? 
                        <CustomTag icon={<CheckCircleOutlined />} label="ON" color={"green"}/> 
                        : 
                        <CustomTag icon={<CloseOutlined />} label="OFF" color={"red"}/>
                    }
                    
                </Col>
            </Row>
        
            <Row>
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>LGSO Mode:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    {
                        <Text style={{ fontWeight:'bolder' }}>{lgsoModeString}</Text>
                    }
                </Col>
                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={labelTextStyle}>Low Grain Shutoff:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <CustomTag label={lowGrain == null ? "ERROR" : lowGrain ? "NO GRAIN" : "GRAIN"} color={lowGrain == null ? "orange" : lowGrain ? "red" : "green"} icon={undefined} />
                </Col>
                <Col className="no-border"></Col>
            </Row>
        
            <Row >
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>Trans Moisture:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text style={valueTextStyle}>{binDto?.dryingOperation?.targetMcPercent} %</Text>
                </Col>
                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={labelTextStyle}>Grain Temp:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text 
                    style={valueTextStyle}>
                        {`${formatNumber(useCelsius ? convertFahrenheitToCelsius(binDto?.dryingOperation?.lastSampleTemperatureF) : binDto?.dryingOperation?.lastSampleTemperatureF, {decimalPlaces: 0, suffix: useCelsius ? " °C" : " °F"})}`}
                    </Text>
                </Col>
            </Row>
        
            <Row>
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>Starting Target Temp:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text style={valueTextStyle}>
                        {`${formatNumber( useCelsius ? convertFahrenheitToCelsius(binDto?.dryingOperation?.currentTargetTemperatureF) : binDto?.dryingOperation?.currentTargetTemperatureF, {decimalPlaces: 0, suffix: useCelsius ? " °C" : " °F"})}`}
                    </Text>
                </Col>
                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={labelTextStyle}>Actual Plenum Temp:</Text>
                </Col>
                <Col span={doubleCellSpan} className="value-cell">
                    <Text style={valueTextStyle}>
                        {`${formatNumber(useCelsius ? convertFahrenheitToCelsius(binDto?.dryingOperation?.actualPlenumTemperatureF) : binDto?.dryingOperation?.actualPlenumTemperatureF, {decimalPlaces: 0, suffix: useCelsius ? " °C" : " °F"})}`}
                    </Text>
                </Col>
            </Row>
        
            <Row>
                <Col span={doubleCellSpan - 1} className="title-cell">
                    <Text style={labelTextStyle}>Low Fire:</Text>
                </Col>

                <Col span={doubleCellSpan} className="value-cell">
                    {binDto?.fans?.some(f => f.lowFireOn === true) ? 
                    <CustomTag icon={<CheckCircleOutlined />} label='ON' color={'green'}/> 
                    : 
                    <CustomTag icon={<CloseOutlined />} label='OFF' color={'red'}/>}
                </Col>

                <Col span={doubleCellSpan} className="title-cell">
                    <Text style={labelTextStyle}>High Fire:</Text>
                </Col>

                <Col span={doubleCellSpan} className="value-cell">
                    {binDto?.fans?.some(f => f.highFireOn === true) ? 
                    <CustomTag icon={<CheckCircleOutlined />} label='ON' color={'green'}/> 
                    : 
                    <CustomTag icon={<CloseOutlined />} label='OFF' color={'red'}/>}
                </Col>

                <Col className="no-border">
                    <Button 
                    type="primary"  
                    style={{ fontSize:'11px', fontWeight:'bold', backgroundColor: '#0958d9'}} 
                    size="small"
                    >
                        TAPESTRIP VIEWER
                    </Button>
                </Col>
            </Row>
      </div>
    )
}


export default DryingPageStateTable;